import { Loading } from "react-migration/components/Loading";
import { SelectionFeature } from "src/js/stores/map/store";
import { SummaryError } from "./components/SummaryError";
import { useChat } from "@ai-sdk/react";
import { Button } from "react-migration/components/Button";
import { StreamingSummary } from "./components/StreamingSummary";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useMemo } from "react";
import { DefaultChatTransport } from "ai";
import { ENVIRONMENT } from "src/js/util/environment";
import { v4 as uuidv4 } from "uuid";
import { router } from "src/js/router/react-router";
import ROUTES from "src/js/router/routes";
import { useExistingPlanningSummary } from "react-migration/layouts/map/Multilayer/layer_types/PlanningApplicationsLayerType/PlanningApplicationsSummary/useExistingPlanningSummary";

type PlanningApplicationsSummaryType = {
  selection: SelectionFeature;
  setDetailSelection: (selection: SelectionFeature) => void;
  cardName: string;
};

export const PlanningApplicationsStreamingSummary = ({
  selection,
  setDetailSelection,
  cardName,
}: PlanningApplicationsSummaryType) => {
  const { t } = useTranslation();
  const { setRecentSummaryForSite, recentSummaryMarkdown } = useExistingPlanningSummary({ siteId: selection?.id });

  const onOpenInEditor = (text: string) => {
    if (!selection?.id) {
      return;
    }

    setRecentSummaryForSite(text);
    router.push({
      name: ROUTES.EDITOR,
      query: {
        siteId: selection.id,
      },
    });
  };

  const requestId = useMemo(() => uuidv4(), []);

  const { messages, sendMessage, status, setMessages, error } = useChat({
    id: requestId,
    messages: [],
    transport: new DefaultChatTransport({
      api: `${ENVIRONMENT.SUPPORTING_REST_SERVICE_URL}/planning/summary`,
    }),
  });

  if (error) {
    return <SummaryError />;
  }

  if (recentSummaryMarkdown) {
    const isInEditorRoute = router.currentRoute()?.name === ROUTES.EDITOR;

    return (
      <div className="relative p-2">
        <StreamingSummary
          content={[{ id: uuidv4(), role: "assistant", parts: [{ type: "text", text: recentSummaryMarkdown }] }]}
          selection={selection}
          setDetailSelection={setDetailSelection}
          generating={false}
          onOpenInEditor={isInEditorRoute ? undefined : onOpenInEditor}
        />
      </div>
    );
  }

  if (!messages.length) {
    return (
      <div data-testid="generate-summary-wrapper" className="p-3 flex flex-col w-full items-center">
        <Button
          data-testid="generate-summary-button"
          onClick={() => {
            setMessages([]);
            sendMessage(undefined, { body: { geometry: selection?.feature?.geometry ?? null } });
            logEventV2({
              name: WorkbenchEventName.WORKBENCH_CARD_CLICK,
              properties: {
                cardName: cardName,
                action: "generate",
                clickDetail: "Generate Summary",
              },
            });
          }}
          disabled={status !== "ready"}
          leadIcon="lt-sparkling-line"
        >
          {t("planning.ai.generate")}
        </Button>
      </div>
    );
  }

  if (status === "submitted") {
    return (
      <div className="p-6">
        <Loading />
      </div>
    );
  }

  return (
    <div className="relative p-2">
      <StreamingSummary
        content={messages}
        selection={selection}
        setDetailSelection={setDetailSelection}
        generating={status !== "error" && status !== "ready"}
        onOpenInEditor={(text: string) => onOpenInEditor(text)}
      />
    </div>
  );
};
